import {
  ACTION_LABELS,
  type AuditEntryDto,
  BUILDING_TYPES,
  CASE_SOURCES,
  type CaseDto,
  formatPhone,
  LOST_REASONS,
  type MeDto,
  NOTE_KINDS,
  type OfferListItemDto,
  type CaseDocumentDto,
  type CaseFieldPhotoDto,
  type ContractListItemDto,
  type TaskDto,
  type StartConditionsDto,
  type NoteKind,
  type Page,
  PRIORITIES,
  type ScopeDto, datePl } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, Card, Grid, Group, Menu, Modal, Paper, Select, SimpleGrid, Stack, Tabs, Text, Textarea, Title, SegmentedControl } from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconDots,
  IconNotes,
  IconMapPin,
  IconPencil,
  IconPhone,
  IconPlus,
  IconTrash,
  IconUser,
  IconPlayerPause,
  IconRefresh,
  IconX,
  IconThumbDown,
  IconCopy,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../api';
import { CaseFlagBadges, CaseStatusBadge, CompanyBadgeById, ExecutionStateBadge, PriorityBadge, SalesStageBadge, TradeBadge, VersionStatusBadge } from '../../components/Badges';
import { NextAction, useNextActionHref } from '../CasesPage';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { PageHeader } from '../../components/PageHeader';
import { ReasonDialog } from '../planning/common';
import { useCase, useCaseMutation, useTrades } from '../../data';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { RealizationDialog } from '../planning/RealizationDialog';
import { OffersTab } from './OffersTab';
import { ContractsTab, PaymentsTab } from './ContractsTab';
import { TasksTab } from './TasksTab';
import { TimelineTab } from './TimelineTab';
import { SummaryTab } from './SummaryTab';
import { ProcessHistory } from './ProcessTab';
import { HoldModal, IntakePanel } from './IntakePanel';
import { ProcessTab } from './ProcessTab';
import { NowCard, useNowTasks } from './NowCard';
import { DocumentsTab } from './DocumentsTab';
import { MaterialsTab } from './MaterialsTab';
import { can } from '../../auth';
import { Changes } from '../AuditPage';
import {
  AddScopeForm,
  CloseForm,
  LostCloseForm,
  CompleteForm,
  ReopenForm,
  EditCaseForm,
  OwnerForm,
  ScopeCompanyForm,
  ScopeDescriptionForm,
  ScopeStageForm,
} from './CaseDialogs';
import { ask } from '../../confirm';

type Dialog =
  | { type: 'edit' }
  | { type: 'owner' }
  | { type: 'close' }
  | { type: 'lost' }
  | { type: 'complete' }
  | { type: 'reopen' }
  | { type: 'addScope' }
  | { type: 'scopeDesc'; scope: ScopeDto }
  | { type: 'scopeCompany'; scope: ScopeDto }
  | { type: 'scopeStage'; scope: ScopeDto; action: 'przegrany' | 'wstrzymaj' | 'wznow' };

// docs/10 §4: Podsumowanie → Zadania → Oferty → Umowy → Prace i terminy → Dokumenty → Płatności → Historia
// zadania — praca biura w sprawie (zadania procesowe); prace — prace ekip w terenie (montaż, wizja…)
const CASE_TABS = ['podsumowanie', 'zadania', 'oferty', 'umowy', 'platnosci', 'prace', 'materialy', 'dokumenty', 'historia'];
/** dawne adresy zakładek (linki w powiadomieniach, zakładki przeglądarki) */
const TAB_ALIASES: Record<string, string> = { przebieg: 'podsumowanie', 'os-czasu': 'historia', obieg: 'zadania' };

const DIALOG_TITLES: Record<Dialog['type'], string> = {
  edit: 'Edycja danych sprawy',
  owner: 'Zmiana opiekuna sprawy',
  close: 'Odrzucenie zapytania',
  lost: 'Zamknięcie sprawy jako przegranej',
  complete: 'Zamknięcie sprawy — warunki',
  reopen: 'Ponowne otwarcie sprawy',
  addScope: 'Nowy zakres prac',
  scopeDesc: 'Opis zakresu prac',
  scopeCompany: 'Zmiana firmy zakresu',
  scopeStage: 'Etap zakresu',
};

function ScopeCard({
  c,
  scope,
  open,
  offers,
  cond,
  canWaive,
  onWaiver,
}: {
  c: CaseDto;
  scope: ScopeDto;
  open: (d: Dialog) => void;
  offers: OfferListItemDto[];
  cond: StartConditionsDto | undefined;
  canWaive: boolean;
  onWaiver: (action: 'nadaj' | 'cofnij') => void;
}) {
  const trades = useTrades();
  const trade = trades.data?.find((t) => t.code === scope.tradeCode);
  const lost = scope.salesStage === 'przegrany';
  const held = scope.salesStage === 'wstrzymany';
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/scopes/${scope.id}`, { method: 'DELETE' }));
  const editable = c.can.edit && !c.closedAt;

  return (
    <Card padding="md" style={{ borderTop: `3px solid ${trade?.color ?? 'var(--mantine-color-gray-6)'}`, opacity: lost ? 0.65 : 1 }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={4}>
          <Group gap={6}>
            <TradeBadge code={scope.tradeCode} />
            <Text fw={700}>{trade?.name}</Text>
          </Group>
          <CompanyBadgeById id={scope.companyId} full />
        </Stack>
        {!c.closedAt && (editable || c.can.markLost || c.can.changeCompany) && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" aria-label="Akcje zakresu">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {editable && <Menu.Item onClick={() => open({ type: 'scopeDesc', scope })}>Opis zakresu…</Menu.Item>}
              {c.can.changeCompany && !c.closedAt && (
                <Menu.Item onClick={() => open({ type: 'scopeCompany', scope })} disabled={scope.hasIssuedDocuments}>
                  Zmień firmę…
                </Menu.Item>
              )}
              {editable && !held && !lost && <Menu.Item onClick={() => open({ type: 'scopeStage', scope, action: 'wstrzymaj' })}>Wstrzymaj…</Menu.Item>}
              {(held ? editable : lost && c.can.markLost) && <Menu.Item onClick={() => open({ type: 'scopeStage', scope, action: 'wznow' })}>Wznów…</Menu.Item>}
              {c.can.markLost && !lost && !c.closedAt && (
                <Menu.Item color="red" onClick={() => open({ type: 'scopeStage', scope, action: 'przegrany' })}>
                  Oznacz jako przegrany…
                </Menu.Item>
              )}
              {editable && (
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    void ask(`Usunąć zakres „${trade?.name}” i jego niewykonane kroki?`, { title: "Usunąć zakres?", confirm: "Usuń", danger: true }).then((ok) => ok && m.mutate(undefined));
                  }}
                >
                  Usuń zakres
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      <SimpleGrid cols={2} spacing={4} mt="md" verticalSpacing={8} style={{ alignItems: 'center' }}>
        <Text size="xs" c="dimmed" fw={500}>
          Sprzedaż
        </Text>
        <div>
          {cond?.contractBasis === 'oferta' && scope.salesStage === 'umowa_podpisana' ? (
            <Badge variant="light" color="teal">
              Zlecenie wg oferty
            </Badge>
          ) : (
            <SalesStageBadge stage={scope.salesStage} />
          )}
        </div>
        <Text size="xs" c="dimmed" fw={500}>
          Realizacja
        </Text>
        <div>
          {cond ? (
            <ExecutionStateBadge state={cond.executionState} />
          ) : (
            <Text size="xs" c="dimmed">
              —
            </Text>
          )}
        </div>
      </SimpleGrid>
      {lost && scope.lostReason && (
        <Text size="xs" c="red" mt={4}>
          Powód: {LOST_REASONS[scope.lostReason]}
          {scope.stageNote ? ` — ${scope.stageNote}` : ''}
        </Text>
      )}
      {held && (
        <Text size="xs" c="var(--app-text-yellow)" mt={4}>
          Wstrzymany{scope.onHoldUntil ? ` do ${datePl(scope.onHoldUntil)}` : ''}
          {scope.stageNote ? ` — ${scope.stageNote}` : ''}
        </Text>
      )}
      {scope.description && (
        <Text size="sm" mt="xs">
          {scope.description}
        </Text>
      )}
      {offers.length > 0 && (
        <Stack gap={2} mt="xs">
          {offers.map((o) => (
            <Group key={o.id} gap={6}>
              <Anchor component={Link} to={`/oferty/${o.id}`} size="xs">
                {o.latest.issuedNumber ?? 'oferta robocza'}
              </Anchor>
              <VersionStatusBadge status={o.latest.effectiveStatus} size="xs" caseClosed={!!c.closedAt} />
              {o.acceptance && (
                <Text size="xs" c="green">
                  wariant {o.acceptance.variantName}
                </Text>
              )}
            </Group>
          ))}
        </Stack>
      )}
      {cond && cond.executionState !== 'nie_dotyczy' && (
        <Stack gap={4} mt="sm" p="xs" style={{ background: 'var(--app-muted)', borderRadius: 8 }}>
          {cond.items.map((i) => (
            <Group key={i.label} gap={6} wrap="nowrap" align="flex-start">
              {i.ok ? <IconCircleCheckFilled size={15} color="var(--mantine-color-green-6)" style={{ flexShrink: 0, marginTop: 1 }} /> : <IconCircleXFilled size={15} color="var(--mantine-color-red-6)" style={{ flexShrink: 0, marginTop: 1 }} />}
              <Text size="xs">
                <b>{i.label}</b>: {i.detail}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
      {cond?.waiver && (
        <Text size="xs" c="var(--app-text-orange)" mt={4}>
          Odstępstwo ({cond.waiver.grantedByName}): {cond.waiver.reason}
          {cond.waiver.until ? ` · do ${datePl(cond.waiver.until)}` : ''}
        </Text>
      )}
      {canWaive && !lost && (cond?.waiver ? (
        <Anchor component="button" type="button" size="xs" mt={4} onClick={() => onWaiver('cofnij')}>
          Cofnij odstępstwo
        </Anchor>
      ) : (
        cond && !cond.met && (
          <Anchor component="button" type="button" size="xs" mt={4} onClick={() => onWaiver('nadaj')}>
            Odstępstwo od warunków startu…
          </Anchor>
        )
      ))}
    </Card>
  );
}

function NotesPanel({ c }: { c: CaseDto }) {
  const [kind, setKind] = useState<NoteKind>('telefon');
  const [body, setBody] = useState('');
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: () => api(`/cases/${c.id}/notes`, { method: 'POST', body: { kind, body } }),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey: ['case', c.id] });
      void qc.invalidateQueries({ queryKey: ['case-history', c.id] });
    },
    onError: notifyError,
  });
  return (
    <Stack gap="sm">
      <Paper withBorder p="sm">
        <Stack gap="xs">
          <Select size="xs" data={Object.entries(NOTE_KINDS).map(([value, label]) => ({ value, label }))} value={kind} onChange={(v) => v && setKind(v as NoteKind)} allowDeselect={false} />
          <Textarea placeholder="Co ustalono?" autosize minRows={2} value={body} onChange={(e) => setBody(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button size="xs" onClick={() => add.mutate()} loading={add.isPending} disabled={!body.trim()}>
              Dodaj notatkę
            </Button>
          </Group>
        </Stack>
      </Paper>
      {c.notes.map((n) => (
        <Paper key={n.id} p="xs" withBorder radius="sm">
          <Group justify="space-between" gap={4}>
            <Badge size="xs" variant="light">
              {NOTE_KINDS[n.kind]}
            </Badge>
            <Text size="xs" c="dimmed">
              {formatDateTime(n.createdAt)} · {n.authorName ?? 'system'}
            </Text>
          </Group>
          <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
            {n.body}
          </Text>
        </Paper>
      ))}
      {c.notes.length === 0 && <EmptyState compact title="Brak notatek" hint="Zapisz ustalenia z rozmów z klientem." />}
    </Stack>
  );
}

/**
 * Historia sprawy w jednym miejscu (docs/10 §4): zdarzenia (oferty, umowy, faktury, protokoły — z plikami),
 * zadania procesowe (przejęcia, przekazania, zakończenia) i pełny dziennik operacji.
 */
function CaseHistory({ c }: { c: CaseDto }) {
  const [view, setView] = useState<'zdarzenia' | 'zadania' | 'operacje'>('zdarzenia');
  return (
    <Stack gap="sm">
      <SegmentedControl
        size="xs"
        value={view}
        onChange={(v) => setView(v as typeof view)}
        data={[
          { value: 'zdarzenia', label: 'Zdarzenia' },
          { value: 'zadania', label: 'Zadania' },
          { value: 'operacje', label: 'Dziennik operacji' },
        ]}
        style={{ alignSelf: 'flex-start' }}
      />
      {view === 'zdarzenia' && <TimelineTab c={c} />}
      {view === 'zadania' && <ProcessHistory caseId={c.id} />}
      {view === 'operacje' && <HistoryTab caseId={c.id} />}
    </Stack>
  );
}

function HistoryTab({ caseId }: { caseId: string }) {
  const q = useQuery({ queryKey: ['case-history', caseId], queryFn: () => api<Page<AuditEntryDto>>(`/cases/${caseId}/history?limit=200`) });
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Stack gap={4}>
        {q.data?.items.map((e) => (
          <Group key={e.id} gap="sm" wrap="wrap" align="flex-start" py={4} style={{ borderBottom: '1px solid var(--app-line)' }}>
            <Text size="xs" c="dimmed" w={110} style={{ flexShrink: 0 }}>
              {formatDateTime(e.at)}
            </Text>
            <Text size="xs" w={140} style={{ flexShrink: 0 }}>
              {e.actorName ?? 'system'}
            </Text>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Text size="sm">{ACTION_LABELS[e.action] ?? e.action}</Text>
              {e.reason && (
                <Text size="xs" c="dimmed">
                  Powód: {e.reason}
                </Text>
              )}
              {e.changes != null && <Changes value={e.changes} />}
            </div>
          </Group>
        ))}
      </Stack>
    </QueryState>
  );
}

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <Text size="11px" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.04em' }} mb={2}>
      {label}
    </Text>
    <Text size="sm" component="div">
      {children}
    </Text>
  </div>
);

export function CasePage({ me }: { me: MeDto }) {
  const { id = '' } = useParams();
  const q = useCase(id);
  const offersQ = useQuery({ queryKey: ['case-offers', id], queryFn: () => api<OfferListItemDto[]>(`/cases/${id}/offers`), enabled: can(me, 'oferty.podglad') });
  // Liczniki w tytułach zakładek — te same klucze i endpointy co w zakładkach (wspólny cache, bez podwójnych zapytań).
  const contractsQ = useQuery({ queryKey: ['case-contracts', id], queryFn: () => api<ContractListItemDto[]>(`/cases/${id}/contracts`), enabled: can(me, 'umowy.podglad') });
  const tasksQ = useQuery({ queryKey: ['case-tasks', id], queryFn: () => api<TaskDto[]>(`/cases/${id}/tasks`), enabled: can(me, 'sprawy.podglad') });
  const documentsQ = useQuery({ queryKey: ['case-documents', id], queryFn: () => api<CaseDocumentDto[]>(`/cases/${id}/documents`), enabled: can(me, 'sprawy.podglad') });
  const fieldPhotosQ = useQuery({ queryKey: ['case-field-photos', id], queryFn: () => api<CaseFieldPhotoDto[]>(`/cases/${id}/field-photos`), enabled: can(me, 'sprawy.podglad') });
  const tabCount = (n: number | undefined) => (n ? ` (${n})` : '');
  const condQ = useQuery({ queryKey: ['start-conditions', id], queryFn: () => api<StartConditionsDto[]>(`/cases/${id}/start-conditions`), enabled: can(me, 'warunki.podglad') });
  const qcCase = useQueryClient();
  const waiver = useMutation({
    mutationFn: (v: { scopeId: string; reason?: string; revoke?: boolean }) =>
      v.revoke ? api(`/scopes/${v.scopeId}/waiver/revoke`, { method: 'POST' }) : api(`/scopes/${v.scopeId}/waiver`, { method: 'POST', body: { reason: v.reason, until: null } }),
    onSuccess: (_r, v) => {
      void qcCase.invalidateQueries({ queryKey: ['start-conditions', id] });
      void qcCase.invalidateQueries({ queryKey: ['case-history', id] });
      notifyOk(v.revoke ? 'Cofnięto odstępstwo' : 'Udzielono odstępstwa');
    },
    onError: notifyError,
  });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);
  const [waiveFor, setWaiveFor] = useState<string | null>(null);
  const [holding, setHolding] = useState(false);
  // ?zakladka=oferty|umowy|… — otwarcie zakładki z linku (np. z podpowiedzi „Co teraz?”);
  // ?nowa-oferta=<zakres> / ?nowa-umowa=<zakres> — otwiera okno tworzenia (dokument powstaje dopiero po kliknięciu w oknie)
  const [params, setParams] = useSearchParams();
  // dawny link do pracy ekipy: ?zakladka=zadania&zadanie=… (dziś zakładka „prace”)
  const rawTab = params.get('zakladka') === 'zadania' && params.get('zadanie') ? 'prace' : (params.get('zakladka') ?? '');
  const tab = CASE_TABS.includes(TAB_ALIASES[rawTab] ?? rawTab) ? (TAB_ALIASES[rawTab] ?? rawTab) : 'podsumowanie';
  const setTab = (v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (!v || v === 'podsumowanie') n.delete('zakladka');
        else n.set('zakladka', v);
        n.delete('nowa-oferta');
        n.delete('nowa-umowa');
        n.delete('zadanie');
        return n;
      },
      { replace: true },
    );
  const clearParam = (...names: string[]) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const name of names) n.delete(name);
        return n;
      },
      { replace: true },
    );
  const nextHref = useNextActionHref(q.data?.next, q.data);
  // pasek „Co teraz?”: kilka zadań tej osoby albo podpowiedź, którą może wykonać
  const nowTasks = useNowTasks(id, me);
  const showNow = nowTasks.length >= 2 || !!(q.data?.next && q.data.next.tone !== 'ok' && (!q.data.next.action || nextHref));
  // ?realizacja=1 (z podpowiedzi „Zaplanuj realizację”) — wybór branż i utworzenie zadań, potem planowanie terminu
  const navigate = useNavigate();
  const wantsRealization = params.get('realizacja') === '1';
  // ?zakonczenie=1 (z podpowiedzi „Wszystko odebrane”) — otwiera okno zakończenia sprawy
  const wantsComplete = params.get('zakonczenie') === '1';
  useEffect(() => {
    if (wantsComplete && q.data && !q.data.closedAt && q.data.can.edit) {
      setDialog({ type: 'complete' });
      clearParam('zakonczenie');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsComplete, q.data?.id]);
  // ?zamkniecie=przegrana (podpowiedź / powiadomienie „wszystkie zakresy przegrane”) — okno zamknięcia jako przegranej
  const wantsLost = params.get('zamkniecie') === 'przegrana';
  useEffect(() => {
    if (wantsLost && q.data) {
      if (!q.data.closedAt && q.data.can.edit) setDialog({ type: 'lost' });
      clearParam('zamkniecie');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsLost, q.data?.id]);

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {wantsRealization && q.data && !q.data.closedAt && (
        <RealizationDialog
          caseId={q.data.id}
          preselect={params.get('zakres') ? [params.get('zakres')!] : undefined}
          onClose={() => clearParam('realizacja', 'zakres')}
          onDone={(tasks) => {
            navigate(`/planowanie?zaplanuj=${tasks.map((t) => t.id).join(',')}`);
          }}
        />
      )}
      {q.data &&
        (() => {
          const c = q.data;
          const contact = c.onsiteContact;
          // przyjęcie w toku: odrzucenie zapytania jest w panelu przyjęcia — nie dublujemy go w menu
          const intakeDecides =
            !c.closedAt && ['nowa', 'weryfikacja', 'do_uzupelnienia'].includes(c.intake.status) && (c.intake.can.decide || (c.intake.status === 'nowa' && c.intake.can.claim));
          return (
            <>
              <PageHeader
                title={c.title}
                crumbs={[{ label: 'Sprawy', to: '/sprawy' }, { label: c.number }]}
                badge={
                  <>
                    <CaseStatusBadge status={c.status} size="md" />
                    <PriorityBadge priority={c.priority} />
                    {c.flags.length > 0 && <CaseFlagBadges flags={c.flags} size="sm" />}
                  </>
                }
                actions={
                  <>
                    {c.can.edit && !c.closedAt && (
                      <Button variant="light" leftSection={<IconPencil size={16} />} onClick={() => setDialog({ type: 'edit' })}>
                        Dane sprawy
                      </Button>
                    )}
                    {(c.can.edit || c.can.hold || c.can.reopen) && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="default" size="lg" aria-label="Więcej">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {c.can.hold && !c.hold && (
                            <Menu.Item leftSection={<IconPlayerPause size={16} />} onClick={() => setHolding(true)}>
                              Wstrzymaj sprawę…
                            </Menu.Item>
                          )}
                          {c.closedAt ? (
                            c.can.reopen && (
                            <Menu.Item leftSection={<IconRefresh size={16} />} onClick={() => setDialog({ type: 'reopen' })}>
                              Otwórz ponownie…
                            </Menu.Item>
                            )
                          ) : !c.can.edit ? null : (
                            <>
                              <Menu.Item leftSection={<IconCircleCheck size={16} />} onClick={() => setDialog({ type: 'complete' })}>
                                Zamknij sprawę (zrealizowana)…
                              </Menu.Item>
                              <Menu.Item color="red" leftSection={<IconThumbDown size={16} />} onClick={() => setDialog({ type: 'lost' })}>
                                Zamknij jako przegraną…
                              </Menu.Item>
                              {!intakeDecides && (
                                <Menu.Item color="red" leftSection={<IconX size={16} />} onClick={() => setDialog({ type: 'close' })}>
                                  Odrzuć zapytanie…
                                </Menu.Item>
                              )}
                            </>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </>
                }
              />
              <Group gap="xs" mt={-8} mb="md" wrap="wrap">
                <Button component={Link} to={`/klienci/${c.client.id}`} variant="default" size="xs" radius="xl" leftSection={<IconUser size={14} />}>
                  {c.client.displayName}
                </Button>
                {c.client.phones[0] && (
                  <Button component="a" href={`tel:${c.client.phones[0]}`} variant="default" size="xs" radius="xl" leftSection={<IconPhone size={14} />}>
                    {formatPhone(c.client.phones[0])}
                  </Button>
                )}
                <Button component={Link} to={`/lokalizacje/${c.site.id}`} variant="default" size="xs" radius="xl" leftSection={<IconMapPin size={14} />}>
                  {c.site.address}
                </Button>
              </Group>

              {c.closedAt &&
                (c.closeKind === 'zrealizowana' ? (
                  <Alert color="eko" variant="light" mb="md" icon={<IconCircleCheck size={18} />}>
                    Sprawa zrealizowana — zakończona {formatDateTime(c.closedAt)}
                    {c.closeReason && c.closeReason !== 'Zrealizowano' ? `: ${c.closeReason}` : ''}
                  </Alert>
                ) : c.closeKind === 'duplikat' && c.intake.duplicateOf ? (
                  // P14: duplikat — link do sprawy głównej
                  <Alert color="yellow" variant="light" mb="md" icon={<IconCopy size={18} />} title={<>Zamknięto jako duplikat sprawy <Anchor component={Link} to={`/sprawy/${c.intake.duplicateOf.id}`} fw={700}>{c.intake.duplicateOf.number}</Anchor></>}>
                    {formatDateTime(c.closedAt)}
                    {c.closeReason && c.closeReason !== `Duplikat sprawy ${c.intake.duplicateOf.number}` ? `: ${c.closeReason}` : ''} — dalsza obsługa w sprawie głównej.
                  </Alert>
                ) : c.closeKind === 'przegrana' ? (
                  <Alert color="red" variant="light" mb="md" icon={<IconThumbDown size={18} />}>
                    Sprawa przegrana — zamknięta {formatDateTime(c.closedAt)}: {c.closeReason}
                  </Alert>
                ) : (
                  <Alert color="dark" variant="light" mb="md">
                    Zapytanie zamknięte {formatDateTime(c.closedAt)}: {c.closeReason}
                  </Alert>
                ))}
              {c.intake.duplicates.length > 0 && (
                <Alert color="gray" variant="light" mb="md" icon={<IconCopy size={18} />}>
                  Dołączone zgłoszenia (duplikaty):{' '}
                  {c.intake.duplicates.map((d, i) => (
                    <span key={d.id}>
                      {i > 0 && ', '}
                      <Anchor component={Link} to={`/sprawy/${d.id}`} size="sm">
                        {d.number}
                      </Anchor>
                    </span>
                  ))}
                </Alert>
              )}
              <IntakePanel c={c} />
              {c.status === 'weryfikacja_koncowa' && !c.closedAt && (
                <Alert mb="md" variant="light" color="cyan" icon={<IconCircleCheck size={18} />} title="Weryfikacja końcowa">
                  <Group justify="space-between" gap="xs">
                    <Text size="sm">Prace i odbiory są zakończone. Opiekun sprawdza warunki zamknięcia i potwierdza weryfikację — sprawa zamknie się sama, gdy wszystko będzie spełnione.</Text>
                    <Button size="xs" onClick={() => setDialog({ type: 'complete' })}>
                      Warunki zamknięcia
                    </Button>
                  </Group>
                </Alert>
              )}
              {holding && <HoldModal c={c} onClose={() => setHolding(false)} />}
              {!c.can.edit && !c.closedAt && (
                <Alert variant="light" mb="md">
                  Podgląd — sprawę edytuje jej opiekun ({c.owner?.fullName ?? 'brak'}) lub koordynator. Możesz dodawać notatki.
                </Alert>
              )}

              <Card padding="md" mb="lg">
                <SimpleGrid cols={{ base: 2, md: 3, xl: 6 }} spacing="lg">
                  <Field label="Opiekun">
                    <Group gap={4}>
                      {c.owner?.fullName ?? '—'}
                      {/* QA6 P14: nieobecny opiekun — kto zastępuje i do kiedy */}
                      {c.ownerAbsence && (
                        <Text span size="xs" c="orange">
                          (nieobecność{c.ownerAbsence.deputy ? `, zastępuje ${c.ownerAbsence.deputy.fullName}` : ', bez zastępstwa'} do {datePl(c.ownerAbsence.until).slice(0, 5)})
                        </Text>
                      )}
                      {c.can.changeOwner && !c.closedAt && (
                        <Anchor component="button" type="button" size="xs" onClick={() => setDialog({ type: 'owner' })}>
                          zmień
                        </Anchor>
                      )}
                    </Group>
                  </Field>
                  <Field label="Kontakt na miejscu">
                    {contact ? (
                      <>
                        {contact.fullName} {contact.role && <Text span c="dimmed" size="xs">({contact.role})</Text>}
                        {contact.phone && (
                          <Group gap={2}>
                            <IconPhone size={12} />
                            <Text size="xs">{formatPhone(contact.phone)}</Text>
                          </Group>
                        )}
                      </>
                    ) : (
                      'klient'
                    )}
                  </Field>
                  <Field label="Inwestor">{c.investor?.displayName ?? 'jak klient'}</Field>
                  <Field label="Źródło">
                    {CASE_SOURCES[c.source]}
                  </Field>
                  <Field label="Obiekt">
                    {c.site.buildingType ? BUILDING_TYPES[c.site.buildingType] : '—'}
                    {c.site.usableArea ? `, ${c.site.usableArea} m²` : ''}
                  </Field>
                  {/* gdy jest karta „Co teraz?”, nie powtarzamy tego samego tutaj */}
                  {!showNow && (
                    <Field label="Następny krok">
                      <NextAction next={c.next} c={c} />
                      {c.next?.auto && (
                        <Text size="xs" c="dimmed">
                          podpowiedź systemu
                        </Text>
                      )}
                    </Field>
                  )}
                </SimpleGrid>
                {c.description && (
                  <Text size="sm" mt="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {c.description}
                  </Text>
                )}
                <Text size="xs" c="dimmed" mt="xs">
                  Priorytet: {PRIORITIES[c.priority]} · utworzono {formatDateTime(c.createdAt)} · zmieniono {formatDateTime(c.updatedAt)}
                </Text>
              </Card>

              {/* karta „Co teraz?” tylko dla osoby, która może wykonać ten krok (bez uprawnień — sama informacja w danych sprawy) */}
              {/* kilka zadań tej osoby — każde w swoim wierszu z własnym przyciskiem („Przejmij” przy zadaniach do przejęcia) */}
              {showNow && <NowCard c={c} tasks={nowTasks} nextHref={nextHref} />}

              <Group justify="space-between" mb="sm">
                <Title order={3}>Zakresy prac</Title>
                {c.can.edit && !c.closedAt && (
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setDialog({ type: 'addScope' })}>
                    Dodaj zakres
                  </Button>
                )}
              </Group>
              {c.scopes.length === 0 && (
                <Card padding="md" mb="lg">
                  <EmptyState compact title="Sprawa nie ma jeszcze zakresów prac" hint={c.can.edit && !c.closedAt ? 'Dodaj zakres — branżę i firmę realizującą.' : undefined} />
                </Card>
              )}
              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} mb={c.scopes.length ? 'lg' : 0}>
                {c.scopes.map((s) => (
                  <ScopeCard
                    key={s.id}
                    c={c}
                    scope={s}
                    open={setDialog}
                    offers={(offersQ.data ?? []).filter((o) => o.scopeIds.includes(s.id))}
                    cond={condQ.data?.find((x) => x.scopeId === s.id)}
                    canWaive={can(me, 'warunki.odstepstwo')}
                    onWaiver={(action) => {
                      if (action === 'cofnij') {
                        void ask('Zadania tego zakresu znów będą zależne od warunków startu.', { title: 'Cofnąć odstępstwo od warunków startu?', confirm: 'Cofnij odstępstwo', danger: true }).then((ok) => ok && waiver.mutate({ scopeId: s.id, revoke: true }));
                      } else setWaiveFor(s.id);
                    }}
                  />
                ))}
              </SimpleGrid>

              <Grid gap="lg">
                <Grid.Col span={{ base: 12, lg: 8 }}>
                  <Card padding="md">
                  <Tabs value={tab} onChange={setTab} keepMounted={false} styles={{ tab: { flexShrink: 0 } }}>
                    {/* na wąskim ekranie zakładki przewijają się w poziomie zamiast łamać w kilka rzędów */}
                    <Tabs.List mb="md" style={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin' }}>
                      <Tabs.Tab value="podsumowanie">Podsumowanie</Tabs.Tab>
                      <Tabs.Tab value="zadania">Zadania</Tabs.Tab>
                      {can(me, 'oferty.podglad') ? (
                        <Tabs.Tab value="oferty">Oferty{tabCount(offersQ.data?.length)}</Tabs.Tab>
                      ) : null}
                      {can(me, 'umowy.podglad') ? <Tabs.Tab value="umowy">Umowy{tabCount(contractsQ.data?.filter((k) => k.status !== 'anulowany').length)}</Tabs.Tab> : null}
                      {can(me, 'finanse.podglad') ? <Tabs.Tab value="platnosci">Płatności</Tabs.Tab> : null}
                      {can(me, 'sprawy.podglad') ? <Tabs.Tab value="prace">Prace i terminy{tabCount(tasksQ.data?.filter((t) => t.status !== 'anulowane').length)}</Tabs.Tab> : null}
                      {can(me, 'zapotrzebowania.podglad') ? <Tabs.Tab value="materialy">Materiały</Tabs.Tab> : null}
                      {can(me, 'sprawy.podglad') ? <Tabs.Tab value="dokumenty">Dokumenty i zdjęcia{tabCount((documentsQ.data?.length ?? 0) + (fieldPhotosQ.data?.length ?? 0))}</Tabs.Tab> : null}
                      <Tabs.Tab value="historia">Historia</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="podsumowanie">
                      <SummaryTab c={c} />
                    </Tabs.Panel>
                    <Tabs.Panel value="oferty">
                      <OffersTab c={c} me={me} newFor={params.get('nowa-oferta')} onNewHandled={() => clearParam('nowa-oferta')} />
                    </Tabs.Panel>
                    <Tabs.Panel value="umowy">
                      <ContractsTab c={c} me={me} newFor={params.get('nowa-umowa')} onNewHandled={() => clearParam('nowa-umowa')} />
                    </Tabs.Panel>
                    <Tabs.Panel value="platnosci">
                      <PaymentsTab c={c} />
                    </Tabs.Panel>
                    <Tabs.Panel value="prace">
                      <TasksTab c={c} me={me} />
                    </Tabs.Panel>
                    <Tabs.Panel value="materialy">{tab === 'materialy' && can(me, 'zapotrzebowania.podglad') && <MaterialsTab c={c} me={me} />}</Tabs.Panel>
                    <Tabs.Panel value="dokumenty">
                      <DocumentsTab c={c} />
                    </Tabs.Panel>
                    <Tabs.Panel value="zadania">
                      <ProcessTab c={c} />
                    </Tabs.Panel>
                    <Tabs.Panel value="historia">
                      <CaseHistory c={c} />
                    </Tabs.Panel>
                  </Tabs>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <SectionCard title="Notatki i kontakty" icon={<IconNotes size={16} />} padding="md">
                    <NotesPanel c={c} />
                  </SectionCard>
                </Grid.Col>
              </Grid>

              {waiveFor && (
                <ReasonDialog
                  title="Odstępstwo od warunków startu"
                  label="Uzasadnienie (zostanie zapisane w historii)"
                  confirm="Udziel odstępstwa"
                  color="orange"
                  busy={waiver.isPending}
                  onClose={() => setWaiveFor(null)}
                  onConfirm={(r) => waiver.mutate({ scopeId: waiveFor, reason: r }, { onSuccess: () => setWaiveFor(null) })}
                />
              )}
              <Modal opened={!!dialog} onClose={close} title={dialog ? DIALOG_TITLES[dialog.type] : ''} size={dialog?.type === 'edit' ? 'lg' : 'md'}>
                {dialog?.type === 'edit' && <EditCaseForm c={c} onDone={close} />}
                {dialog?.type === 'owner' && <OwnerForm c={c} onDone={close} />}
                {dialog?.type === 'close' && <CloseForm c={c} onDone={close} />}
                {dialog?.type === 'lost' && <LostCloseForm c={c} onDone={close} />}
                {dialog?.type === 'complete' && <CompleteForm c={c} onDone={close} />}
                {dialog?.type === 'reopen' && <ReopenForm c={c} onDone={close} />}
                {dialog?.type === 'addScope' && <AddScopeForm c={c} onDone={close} />}
                {dialog?.type === 'scopeDesc' && <ScopeDescriptionForm c={c} scope={dialog.scope} onDone={close} />}
                {dialog?.type === 'scopeCompany' && <ScopeCompanyForm c={c} scope={dialog.scope} onDone={close} />}
                {dialog?.type === 'scopeStage' && <ScopeStageForm c={c} scope={dialog.scope} action={dialog.action} onDone={close} />}
              </Modal>
            </>
          );
        })()}
    </QueryState>
  );
}
